"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";
import { Label } from "./label";

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Label text for the field */
  label: string;
  /** HTML id for the input (for label association) */
  htmlFor: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Help text to display below the input */
  hint?: string;
  /** Whether to show the label */
  showLabel?: boolean;
  /** Orientation of label relative to input */
  orientation?: "vertical" | "horizontal";
  /** The form input element */
  children: React.ReactNode;
}

/**
 * FormField provides a consistent wrapper for form inputs with label, hint, and error display.
 * It handles accessibility attributes automatically.
 */
export function FormField({
  className,
  label,
  htmlFor,
  required = false,
  error,
  hint,
  showLabel = true,
  orientation = "vertical",
  children,
  ...props
}: FormFieldProps) {
  const hasError = !!error;
  const hintId = `${htmlFor}-hint`;
  const errorId = `${htmlFor}-error`;
  const describedBy = hasError ? errorId : hint ? hintId : undefined;

  // Clone children to inject aria attributes
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        id: htmlFor,
        "aria-describedby": describedBy,
        "aria-invalid": hasError || undefined,
        "aria-required": required || undefined,
      });
    }
    return child;
  });

  return (
    <div
      className={cn(
        styleDrivers.formField.root,
        orientation === "horizontal" && "flex items-center gap-4 space-y-0",
        className
      )}
      {...props}
    >
      {showLabel && (
        <Label
          htmlFor={htmlFor}
          className={cn(
            styleDrivers.formField.label,
            orientation === "horizontal" && "min-w-[120px] shrink-0"
          )}
        >
          {label}
          {required && (
            <>
              <div className={styleDrivers.formField.requiredIndicator} aria-hidden="true">*</div>
              <div className="sr-only">(required)</div>
            </>
          )}
        </Label>
      )}

      <div className={cn("flex-1", orientation === "vertical" && "w-full")}>
        {enhancedChildren}

        {hint && !hasError && (
          <p
            id={hintId}
            className={styleDrivers.formField.hint}
          >
            {hint}
          </p>
        )}

        {hasError && (
          <p
            id={errorId}
            role="alert"
            className={styleDrivers.formField.error}
          >
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

FormField.displayName = "FormField";

/**
 * FormFieldGroup groups multiple related fields together.
 */
export interface FormFieldGroupProps extends React.HTMLAttributes<HTMLFieldSetElement> {
  /** Legend/title for the group */
  legend?: string;
  /** Description for the group */
  description?: string;
  /** Whether the legend should be visually hidden */
  hideLegend?: boolean;
  children: React.ReactNode;
}

export function FormFieldGroup({
  className,
  legend,
  description,
  hideLegend = false,
  children,
  ...props
}: FormFieldGroupProps) {
  return (
    <fieldset className={cn("space-y-4", className)} {...props}>
      {legend && (
        <legend
          className={cn(
            "text-sm font-semibold text-foreground",
            hideLegend && "sr-only"
          )}
        >
          {legend}
        </legend>
      )}
      {description && (
        <p className="text-sm text-muted-foreground -mt-2">{description}</p>
      )}
      <div className="space-y-4">{children}</div>
    </fieldset>
  );
}

FormFieldGroup.displayName = "FormFieldGroup";

export default FormField;
